 <template>
  <div class="cso-input-datepicker"
       :class="{'has-error': validationErrors, 'is-disabled': isDisabled, 'is-active': isActive, 'left-opening': opensToTheLeft}">
    <div class="cso-input-datepicker-wrapper">

      <label class="cso-datepicker-label"
             v-if="label"
             :for="id">{{ label }}</label>
      <datepicker v-model="dateValue"
                  placeholder="TT.MM.JJJJ"
                  :id="id"
                  :name="name"
                  :validation-name="validationName"
                  :locale="de"
                  input-format="dd.MM.yyyy"
                  class="cso-datepicker form-control"
                  :class="{'pdf-input': pdfInput}"
                  :disabled="isDisabled"
                  :disabled-dates="disabledDates"
                  :upper-limit="maxDate"
                  :lower-limit="minDate"
                  :starting-view="startingView"
                  :typeable="true"
                  :data-cy="cypressName"
                  @input="onInput"
                  @closed="onClose"
                  @opened="onOpen"
                  ref="datepicker">
      </datepicker>
      <i class="icon--date-picker calendar"
            aria-label="Kalender"
            role="img"></i>
      <i v-if="isNullable && currentValue"
            class="icon--date-picker close-big"
            @click="clear"
            aria-label="Schließen"
            role="img"></i>
    </div>
    <div v-if="showValidationErrors" class="form-explanations">
      <span class="form-helpText"></span>
      <span v-for="(error, index) in validationErrors"
            class="form-errorText"
            :key="index">
        {{error.message}}
      </span>
    </div>
  </div>
</template>
<script>
import Datepicker from 'vue3-datepicker'
import { de } from 'date-fns/locale'

import validatedInputField from '@/mixins/validatedInputField'

export default {
  name: 'CsoDatepicker',
  mixins: [validatedInputField],
  components: {
    Datepicker
  },
  props: {
    name: {
      type: String,
      required: true
    },
    minDate: {
      type: Date,
      required: false
    },
    maxDate: {
      type: Date,
      required: false
    },
    disabledDates: {
      type: [Array, Object],
      required: false
    },
    label: {
      type: String,
      required: false
    },
    isNullable: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    direction: {
      type: String,
      default: 'ascending'
    },
    pdfInput: {
      type: Boolean,
      default: false
    },
    opensToTheLeft: {
      type: Boolean,
      default: false
    },
    cypressName: {
      type: String,
      required: false
    }
  },

  data () {
    return {
      de,
      isActive: false
    }
  },

  computed: {
    dateValue: {
      get () {
        return this.modelValue
      },
      set (value) {
        this.$emit('update:modelValue', value)
      }
    },

    isDisabled () {
      return this.disabled || (!!this.maxDate && !!this.minDate && this.maxDate.getTime() == this.minDate.getTime())
    },

    id () {
      return this.name.replaceAll('.', '-')
    },

    startingView () {
      if (this.direction === 'ascending') {
        return 'day'
      }
      if (this.direction === 'descending') {
        return 'year'
      }
      return undefined
    }
  },

  methods: {
    clear () {
      this.dateValue = null
    },
    onClose () {
      this.$refs.datepicker?.inputRef?.blur()
      this.isActive = false
    },
    onOpen () {
      this.isActive = true
    },
    onInput (event) {
      const value = event.target.value || ''
      const digits = value.replaceAll(/[^\d]/g, '')
      const withDots = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4, 8)}`
      const valueFiltered = withDots.replace(/\.+$/, '')
      this.$refs.datepicker.input = valueFiltered
    }
  }
}
</script>

<style lang="scss">

.cso-input-datepicker {

  i {
    position: absolute;
    z-index: 8;
    top: 13px;
    height: 24px;
    width: 24px;

    &.icon--date-picker {
      right: 14px;
      background-repeat: no-repeat;
      background-position: center;
      background-color: transparent;
      background-size: contain;

      &.calendar {
        background-image: url('/static/icons/calendar.svg');
      }

      &.close-big {
        cursor: pointer;
        top: 14px;
        z-index: 12;
        margin-right: 30px;
        background-image: url('/static/icons/close-big.svg');
      }
    }
  }

  .cso-datepicker {
    z-index: 8;
  }
}

.cso-input-datepicker {
  display: block;
  margin-top: 16px;
  margin-bottom: 16px;
  font-family: $font-family-regular;
  line-height: 1.2em;

  @include xs {
    width: unset;
  }

  input {
    height: 53px;
    border: none;
    padding-left: 12px;

    &:focus {
      outline: 2px solid $blue;
      outline-offset: 2px;
    }

    &::placeholder {
      color: $gray-4d;
    }
  }

  .form-errorText {
    padding: 6px 0;
    font-family: $font-family-regular;
    color: $red-b6;
    display: flex;
    gap: 8px;
    font-size: 14px;

    &:before {
      background-image: url(/static/icons/alert_round.svg);
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      flex: 0 0 16px;
      content: "";
    }
  }

  .form-control {
    color: $black;
    font-size: 18px;
    font-weight: 400;
    line-height: 26px;
    padding-top: 12px;
    font-family: $font-family-regular;
    cursor: pointer;
    background-color: transparent;
  }

  .form-control[disabled] {
    cursor: not-allowed;
    font-size: 16px;
    padding-top: 12px;
    font-family: $font-family-regular;
  }

  .cso-datepicker-label {
    display: block;
    left: 0;
    line-height: 1.6rem;
    margin: 0;
    position: absolute;
    cursor: pointer;
    z-index: 9;
    font-size: 12px;
    background: $white;
    padding: 2px;
    margin-left: 10px;
    top: -8px;
    color: $black;
  }

  &.is-disabled {
    background-color: $gray-f2;

    .cso-datepicker-label {
      cursor: not-allowed;
    }

    .cso-input-datepicker-wrapper {
      border: 1px solid $gray-f2;
    }
  }

  .cso-input-datepicker-wrapper {
    position: relative;
    width: 100%;
    border: 1px solid $gray-4d;
  }
}

.cso-input-datepicker.has-error {

  .cso-datepicker-label {
    color: $red-b6;
  }

  .form-control {
    border: 2px solid $red-b6;
  }

  .cso-input-datepicker-wrapper {
    border: 0;
  }
}


.cso-input-datepicker.left-opening .v3dp__popout {
  @include sm {
    right: 0;
  }
}

.v3dp__popout {
  width: 375px !important;
  padding: 24px !important;
  border: 1px solid $blue !important;
  border-top: 1px solid $gray-dark !important;
  margin-left: -1px !important;
  --heading-hover-color: none;
  --elem-current-outline-color: none;
  --border-radius: none !important;
  z-index: 999 !important;

  @include xs {
    padding: 10px !important;
    width: 230px !important;
  }

  .v3dp__heading {
    font-weight: 400 !important;
    font-size: 18px !important;
    line-height: 24px !important;
    height: 24px !important;

    @include xs {
      font-size: 16px !important;
    }
  }

  .v3dp__subheading {
    color: $gray-dark !important;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .v3dp__divider {
    display: none;
  }

  .v3dp__heading__button {
    width: auto !important;
  }


  .v3dp__elements {
    font-size: 14px !important;
    margin: auto !important;

    button {
      padding: 3px !important
    }

    .v3dp__element__button__year,
    .v3dp__element__button__month {
      margin: auto;
    }

    .v3dp__element__button__day {
      width: 40px !important;

      @include xs {
        width: 30px !important
      }
    }
    button span {
      background-color: white !important;
      color: currentColor !important;
      line-height: 40px !important;
      height: 40px !important;
      width: 40px !important;
      border: 1px solid transparent !important;
      border-radius: 50% !important;

      @include xs {
        line-height: 30px !important;
        height: 30px !important;
        width: 30px !important;
      }
    }

    button.current span {
      font-weight: normal !important;
      border: 1px solid $gray !important;
    }

    button.selected span {
      background-color: $blue !important;
      color: $white !important;
    }

    button:disabled span {
      color: $white !important;
      border: 1px solid $white !important;
    }

    button:not(:disabled):hover span {
      border: 1px solid $blue !important;
    }

    button.v3dp__element__button__year span {
      width: 70px !important;
      height: 40px !important;
      border-radius: 70px !important;
      padding: 8px 16px !important;
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
    }

    .button.v3dp__element__button__year {
      width: 100px !important;
    }
  }
}
</style>
